import apiClient from './api';

/**
 * Servicio para gestionar el examen IPV (Inventario de Personalidad para Vendedores)
 */
export const ipvService = {
  // ==================== CLAVES (Respuestas Correctas) ====================
  
  /**
   * Registrar respuestas correctas en lote
   * @param {Array} claves - Array de objetos con formato: 
   * { pregunta, escala, respuestasCorrectas, dgv, invertida }
   */
  registrarClavesBatch: async (claves) => {
    const response = await apiClient.post('/ivp/claves/batch', claves);
    return response.data;
  },

  /**
   * Listar todas las claves de respuesta registradas
   */
  listarClaves: async () => {
    const response = await apiClient.get('/ivp/claves');
    return response.data;
  },

  // ==================== EXAMEN ====================
  
  /**
   * Crear un nuevo examen IPV
   * @param {number} usuarioId - ID del usuario que crea el examen (obligatorio)
   * @param {Object} data - { ipvEvaluacionId, procesoId, estado, observaciones }
   * @returns {Object} { id, ipvEvaluacionId, usuarioId, usuarioNombre, procesoId, estado, observaciones, fechaCreacion }
   */
  crearExamen: async (usuarioId, data = {}) => {
    const body = {
      ipvEvaluacionId: data.ipvEvaluacionId || null,
      usuarioId: usuarioId,
      procesoId: data.procesoId || null,
      estado: data.estado || 'PENDIENTE',
      observaciones: data.observaciones || ''
    };
    const response = await apiClient.post('/ipv-examenes', body);
    return response.data;
  },

  /**
   * Obtener un examen por ID
   */
  obtenerExamen: async (examenId) => {
    const response = await apiClient.get(`/ipv-examenes/${examenId}`);
    return response.data;
  },

  /**
   * Listar todos los exámenes
   */
  listarExamenes: async () => {
    const response = await apiClient.get('/ipv-examenes');
    return response.data;
  },

  /**
   * Actualizar el estado de un examen
   * @param {number} examenId - ID del examen
   * @param {Object} data - Datos a actualizar (estado, observaciones, etc.)
   */
  actualizarExamen: async (examenId, data) => {
    const response = await apiClient.put(`/ipv-examenes/${examenId}`, data);
    return response.data;
  },

  /**
   * Listar todos los exámenes IPV completados (con resultados)
   * Similar a getTodosLosTestsCompletados de DISC
   * Usa el mismo endpoint que listarExamenes pero filtra por estado COMPLETADO
   */
  getTodosLosExamenesCompletados: async () => {
    const response = await apiClient.get('/ipv-examenes');
    // Filtrar solo exámenes completados
    const examenesCompletados = response.data.filter(examen =>
      examen.estado === 'COMPLETADO' || examen.estado === 'FINALIZADO'
    );
    return examenesCompletados;
  },

  /**
   * Obtener exámenes IPV completados por usuario específico
   * @param {number} usuarioId - ID del usuario
   * @returns {Array} Array de exámenes IPV completados del usuario
   */
  getExamenesCompletadosPorUsuario: async (usuarioId) => {
    const response = await apiClient.get(`/ipv-examenes/usuario/${usuarioId}`);
    return response.data;
  },

  // ==================== EVALUACIÓN ====================
  
  /**
   * Crear una evaluación para un examen específico
   * @param {number} postulanteId - ID del postulante (usuario)
   * @param {Object} data - { ipvExamenId }
   */
  crearEvaluacion: async (postulanteId, data) => {
    const response = await apiClient.post(`/ivp/evaluaciones/crear/${postulanteId}`, data);
    return response.data;
  },

  /**
   * Obtener una evaluación por ID
   */
  obtenerEvaluacion: async (evaluacionId) => {
    const response = await apiClient.get(`/ivp/evaluaciones/${evaluacionId}`);
    return response.data;
  },

  // ==================== RESPUESTAS ====================
  
  /**
   * Guardar respuestas de una evaluación
   * @param {number} evaluacionId - ID de la evaluación
   * @param {Object} respuestas - Objeto con formato: { "1": "B", "2": "A", ... }
   */
  guardarRespuestas: async (evaluacionId, respuestas) => {
    const response = await apiClient.post(`/ivp/respuestas/evaluacion/${evaluacionId}`, respuestas);
    return response.data;
  },

  // ==================== EVALUACIÓN DE RESULTADOS ====================
  
  /**
   * Evaluar los resultados de una evaluación (calcula puntuaciones)
   * POST sin cuerpo
   * @param {number} evaluacionId - ID de la evaluación
   * @returns {Object} Puntuaciones directas (Pd) y transformadas (Pt) por escala
   */
  evaluarResultados: async (evaluacionId) => {
    const response = await apiClient.post(`/ivp/evaluar/evaluacion/${evaluacionId}`);
    return response.data;
  },

  /**
   * Listar resultados de una evaluación
   * GET
   */
  listarResultado: async (evaluacionId) => {
    const response = await apiClient.get(`/ivp/resultado/evaluacion/${evaluacionId}`);
    return response.data;
  },

  /**
   * Obtener resultado interpretado con niveles y descripciones
   * GET (no POST)
   * @param {number} evaluacionId - ID de la evaluación
   * @returns {Object} Resultado completo con interpretaciones por escala
   */
  obtenerResultadoInterpretado: async (evaluacionId) => {
    const response = await apiClient.get(`/ivp/resultado/interpretado/evaluacion/${evaluacionId}`);
    return response.data;
  },

  // ==================== MÉTODOS AUXILIARES ====================
  
  /**
   * Flujo completo: Crear examen, evaluación y preparar para respuestas
   * @param {number} usuarioId - ID del usuario que realiza el examen
   * @returns {Object} { examen, evaluacion }
   */
  iniciarExamenCompleto: async (usuarioId) => {
    try {
      // 1. Crear examen con el usuario
      const examen = await ipvService.crearExamen(usuarioId);
      
      // 2. Crear evaluación para ese examen
      const evaluacion = await ipvService.crearEvaluacion(examen.usuarioId, {
        ipvExamenId: examen.id
      });
      
      return { examen, evaluacion };
    } catch (error) {
      console.error('Error en flujo completo de examen IPV:', error);
      throw error;
    }
  },

  /**
   * Flujo completo: Evaluar y obtener resultado interpretado
   * @param {number} evaluacionId 
   */
  obtenerResultadoCompleto: async (evaluacionId) => {
    try {
      // 1. Evaluar
      await ipvService.evaluarResultados(evaluacionId);
      
      // 2. Obtener resultado interpretado
      const resultado = await ipvService.obtenerResultadoInterpretado(evaluacionId);
      
      return resultado;
    } catch (error) {
      console.error('Error al obtener resultado completo:', error);
      throw error;
    }
  }
};

export default ipvService;
